import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

import InventoryService from "../../../api/inventory";
import { getSuppliers, getCostCategories } from "../../../api/DeliveryCenter/deliveryCenterCosts";

const StockEntryModal = ({ open, onClose, onSuccess, showAlert }) => {
  const [ingredients, setIngredients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [costCategories, setCostCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    ingredient: null,
    quantity: "",
    unit_cost: "",
    supplier: null,
    reference: "",
    notes: "",
    create_cost_entry: false,
    cost_category: null,
  });

  useEffect(() => {
    if (open) {
      loadData();
      setForm({
        ingredient: null,
        quantity: "",
        unit_cost: "",
        supplier: null,
        reference: "",
        notes: "",
        create_cost_entry: false,
        cost_category: null,
      });
    }
  }, [open]);

  const loadData = async () => {
    try {
      const [ingredientsRes, suppliersRes, categoriesRes] = await Promise.all([
        InventoryService.getIngredients({ page_size: 500 }),
        getSuppliers({ page_size: 200 }).catch(() => ({ data: { results: [] } })),
        getCostCategories({ page_size: 200 }).catch(() => ({ data: { results: [] } })),
      ]);
      setIngredients(ingredientsRes.data.results || []);
      setSuppliers(suppliersRes.data.results || suppliersRes.data || []);
      setCostCategories(categoriesRes.data.results || categoriesRes.data || []);
    } catch (err) {
      console.error("Error loading data:", err);
    }
  };

  const handleSubmit = async () => {
    if (!form.ingredient || !form.quantity) return;

    setLoading(true);
    try {
      await InventoryService.createStockEntry({
        ingredient: form.ingredient.id,
        quantity: form.quantity,
        unit_cost: form.unit_cost || null,
        supplier: form.supplier?.id || null,
        reference: form.reference,
        notes: form.notes,
        create_cost_entry: form.create_cost_entry,
        cost_category: form.cost_category?.id || null,
      });
      onSuccess();
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        "Erro ao registrar entrada";
      showAlert(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Nova Entrada de Estoque</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <Autocomplete
              options={ingredients}
              getOptionLabel={(opt) => `${opt.name} (${opt.unit})`}
              value={form.ingredient}
              onChange={(_, val) => setForm({ ...form, ingredient: val })}
              renderInput={(params) => (
                <TextField {...params} label="Ingrediente" required />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={`Quantidade${form.ingredient ? ` (${form.ingredient.unit})` : ""}`}
              type="number"
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              fullWidth
              required
              inputProps={{ min: 0.001, step: 0.001 }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Custo Unitario (R$)"
              type="number"
              value={form.unit_cost}
              onChange={(e) => setForm({ ...form, unit_cost: e.target.value })}
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Atualiza o custo do ingrediente"
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              options={suppliers}
              getOptionLabel={(opt) => opt.name}
              value={form.supplier}
              onChange={(_, val) => setForm({ ...form, supplier: val })}
              renderInput={(params) => (
                <TextField {...params} label="Fornecedor (opcional)" />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="N. Nota Fiscal / Referencia"
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Observacoes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              fullWidth
            />
          </Grid>

          {/* Integration with CostControl */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.create_cost_entry}
                  onChange={(e) =>
                    setForm({ ...form, create_cost_entry: e.target.checked })
                  }
                />
              }
              label="Criar lancamento no Controle de Custos"
            />
          </Grid>
          {form.create_cost_entry && (
            <Grid item xs={12}>
              <Autocomplete
                options={costCategories}
                getOptionLabel={(opt) => opt.name}
                value={form.cost_category}
                onChange={(_, val) => setForm({ ...form, cost_category: val })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Categoria de Custo"
                    required
                    helperText="Selecione a categoria para o lancamento de custo"
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!form.ingredient || !form.quantity || loading}
          sx={{
            backgroundColor: "#16a34a",
            "&:hover": { backgroundColor: "#15803d" },
          }}
        >
          {loading ? "Registrando..." : "Registrar Entrada"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockEntryModal;
